import { Component, forwardRef, Input, OnDestroy, OnInit } from "@angular/core";
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { Dictionary, groupBy } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { InsideSurvey } from "src/app/shared/Models/read";

@Component({
  selector: "app-special-building-info",
  templateUrl: "./special-building-info.component.html",
  styleUrls: ["./special-building-info.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SpecialBuildingInfoComponent),
      multi: true,
    },
    FormBuilder,
  ],
})
export class SpecialBuildingInfoComponent
  implements OnInit, ControlValueAccessor, OnDestroy
{
  @Input() isAuthCanWrite: boolean;

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  specialBuildingCategories: Dictionary<
    {
      description: string;
      triggerFlag: boolean;
      formControlName: string;
      category: string;
    }[]
  >;

  specialBuildingFlags: {
    description: string;
    triggerFlag: boolean;
    formControlName: string;
    category: string;
  }[];
  specialBuildingInfoForm: FormGroup = new FormGroup({});

  formChanges: Subject<Partial<InsideSurvey>> = new Subject<
    Partial<InsideSurvey>
  >();

  constructor(private fb: FormBuilder) {
    this.specialBuildingFlags = [
      {
        description: $localize`:@@specialBuilding.heightOver3M5:Height of +3,50meter`,
        triggerFlag: true,
        formControlName: "heightOver3M50",
        category: $localize`:@@specialBuilding.height:Height`,
      },
      {
        description: $localize`:@@specialBuilding.specificHeightSpecialNeeds:Specific height with special needs`,
        triggerFlag: true,
        formControlName: "specificHeightSpecialNeeds",
        category: $localize`:@@specialBuilding.height:Height`,
      },

      {
        description: $localize`:@@specialBuilding.specificInstructionsAesthetics:Specific instructions related to aesthetics (ceiling moldings, etc.)`,
        triggerFlag: false,
        formControlName: "specificInstructionsAesthetics",
        category: $localize`:@@specialBuilding.cablePlacement:Cable placement`,
      },
      {
        description: $localize`:@@specialBuilding.standardCableDuctsObligatory:Standard cable ducts obligatory (16 x 16 or 32 x 16 on "length to be defined")`,
        triggerFlag: false,
        formControlName: "standardCableDuctsObligatory",
        category: $localize`:@@specialBuilding.cablePlacement:Cable placement`,
      },
      {
        description: $localize`:@@specialBuilding.specialCableDuctOrStandardCableDuctMoreThan20M:Special cable duct or standard cable duct >20meters`,
        triggerFlag: true,
        formControlName: "specialCableDuctOrStandardCableDuctMoreThan20M",
        category: $localize`:@@specialBuilding.cablePlacement:Cable placement`,
      },
      {
        description: $localize`:@@specialBuilding.needForSpeedpipeTypeSheath:Need for a speedpipe type sheath`,
        triggerFlag: true,
        formControlName: "needForSpeedpipeTypeSheath",
        category: $localize`:@@specialBuilding.cablePlacement:Cable placement`,
      },
      {
        description: $localize`:@@specialBuilding.needForFlextubeTypeSheath:Need a flextube type sheath (rodent protection, etc.)`,
        triggerFlag: false,
        formControlName: "needForFlextubeTypeSheath",
        category: $localize`:@@specialBuilding.cablePlacement:Cable placement`,
      },

      {
        description: $localize`:@@specialBuilding.passageThroughExteriorHallway:Passage through an exterior hallway (CCA cable)`,
        triggerFlag: false,
        formControlName: "passageThroughExteriorHallway",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },
      {
        description: $localize`:@@specialBuilding.passageOutsideSpecificEquipment:Passage outside with specific equipment (front cable, etc.)`,
        triggerFlag: true,
        formControlName: "passageOutsideSpecificEquipment",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },
      {
        description: $localize`:@@specialBuilding.passageThroughFalseCeilings:Passage through false ceilings`,
        triggerFlag: false,
        formControlName: "passageThroughFalseCeilings",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },
      {
        description: $localize`:@@specialBuilding.passageThroughFalseFloor:Passage through a false floor`,
        triggerFlag: false,
        formControlName: "passageThroughFalseFloor",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },
      {
        description: $localize`:@@specialBuilding.floorboxNotOnSameFloorAsApartment:Floorbox not on the same floor as the apartment`,
        triggerFlag: false,
        formControlName: "floorboxNotOnSameFloorAsApartment",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },
      {
        description: $localize`:@@specialBuilding.serviceLineConnection:Service line connection (floorbox next to the BDFO)`,
        triggerFlag: false,
        formControlName: "serviceLineConnection",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },
      {
        description: $localize`:@@specialBuilding.severalTechnicalShafts:Several technical shafts that can be used`,
        triggerFlag: false,
        formControlName: "severalTechnicalShafts",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },
      {
        description: $localize`:@@specialBuilding.floorboxPresentInQuadrantC:Floorbox present in a Quadrant C`,
        triggerFlag: false,
        formControlName: "floorboxPresentInQuadrantC",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },
      {
        description: $localize`:@@specialBuilding.extensionOfExistingTechnicalShaft:Extension of the existing technical shaft to be carried out (Length and type of chute to be installed)`,
        triggerFlag: true,
        formControlName: "extensionOfExistingTechnicalShaft",
        category: $localize`:@@specialBuilding.specificCableLocation:Specific cable location`,
      },

      {
        description: $localize`:@@specialBuilding.cableMoreThan50MOr2ManFiber:>50 Meter or 2man Fiber`,
        triggerFlag: true,
        formControlName: "cableMoreThan50MOr2ManFiber",
        category: $localize`:@@specialBuilding.lengthOfCable:Length of cable`,
      },

      {
        description: $localize`:@@specialBuilding.specialDrilling:Special drilling (material / depth / diameter) with specific drill bit`,
        triggerFlag: true,
        formControlName: "specialDrilling",
        category: $localize`:@@specialBuilding.drilling:Drilling`,
      },

      {
        description: $localize`:@@specialBuilding.mivbStibStation:MIVB STIB station`,
        triggerFlag: true,
        formControlName: "mivbStibStation",
        category: $localize`:@@specialBuilding.mivbStibBuilding:MIVB/STIB building`,
      },

      {
        description: $localize`:@@specialBuilding.accessToBuilding:Access to the building - contact with the building manager beforehand`,
        triggerFlag: true,
        formControlName: "accessToBuilding",
        category: $localize`:@@specialBuilding.specialNeedsOnSite:Special needs on site`,
      },

      {
        description: $localize`:@@specialBuilding.shadowportfolioSolution:Shadowportfolio solution`,
        triggerFlag: true,
        formControlName: "shadowportfolioSolution",
        category: $localize`:@@specialBuilding.shadowportfolio:Shadowportfolio`,
      },
    ];

    this.specialBuildingCategories = groupBy(
      this.specialBuildingFlags,
      (flag) => flag.category
    );
  }

  writeValue(obj: InsideSurvey): void {
    this.specialBuildingInfoForm.patchValue({
      ...obj,
    });
  }

  registerOnChange(fn: any): void {
    this.formChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched();
  }

  onTouched = () => null;

  setDisabledState?(isDisabled: boolean): void {
    throw new Error("Method not implemented.");
  }

  ngOnInit(): void {
    this.buildForm();
  }

  toggleMainFlag(): boolean {
    var shouldToggleMainFlag = this.specialBuildingFlags.some((flag) => {
      return (
        flag.triggerFlag &&
        this.specialBuildingInfoForm.get(flag.formControlName).value
      );
    });

    if (
      this.specialBuildingInfoForm.get("specialBuilding").value !=
      shouldToggleMainFlag
    )
      this.specialBuildingInfoForm
        .get("specialBuilding")
        .setValue(shouldToggleMainFlag);

    return shouldToggleMainFlag;
  }

  private buildForm() {
    this.specialBuildingInfoForm = this.fb.group({
      specialBuilding: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      specialBuildingRemark: [[]],
      heightOver3M50: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      specificHeightSpecialNeeds: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      specificInstructionsAesthetics: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      standardCableDuctsObligatory: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      specialCableDuctOrStandardCableDuctMoreThan20M: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      needForSpeedpipeTypeSheath: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      needForFlextubeTypeSheath: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      passageThroughExteriorHallway: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      passageOutsideSpecificEquipment: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      passageThroughFalseCeilings: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      passageThroughFalseFloor: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      floorboxNotOnSameFloorAsApartment: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      serviceLineConnection: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      severalTechnicalShafts: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      floorboxPresentInQuadrantC: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      extensionOfExistingTechnicalShaft: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      cableMoreThan50MOr2ManFiber: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      specialDrilling: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      mivbStibStation: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      accessToBuilding: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      shadowportfolioSolution: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
    });

    this.specialBuildingInfoForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        var specialBuildingFlag = this.toggleMainFlag();
        return this.formChanges.next({
          ...val,
          specialBuilding: specialBuildingFlag,
        });
      });
  }
}
